// Cloud Carbon & GreenOps Calculation Engine
// Based on Green Software Foundation (GSF) SCI, Cloud Carbon Footprint (CCF), and GHG Protocol standards

export const GRID_CARBON_FACTORS = [
  {
    id: 'eu-north-1',
    name: 'Europe (Stockholm / Nordics)',
    country: 'Sweden / Finland',
    carbonIntensity: 24, // gCO2e / kWh (Clean hydro, nuclear, wind)
    renewableShare: 96,
    pue: 1.10,
    rating: 'A+ (Ultra Clean)',
    color: '#2ecc71'
  },
  {
    id: 'eu-west-3',
    name: 'Europe (Paris)',
    country: 'France',
    carbonIntensity: 55, // gCO2e / kWh (Low-carbon nuclear)
    renewableShare: 88,
    pue: 1.14,
    rating: 'A (Low Carbon)',
    color: '#00d2d3'
  },
  {
    id: 'us-west-2',
    name: 'US West (Oregon)',
    country: 'United States',
    carbonIntensity: 178, // gCO2e / kWh (Columbia River hydro blend)
    renewableShare: 72,
    pue: 1.15,
    rating: 'B+ (Hydro Blend)',
    color: '#45aaf2'
  },
  {
    id: 'eu-west-1',
    name: 'Europe (Ireland)',
    country: 'Ireland',
    carbonIntensity: 316, // gCO2e / kWh (Wind + gas)
    renewableShare: 45,
    pue: 1.16,
    rating: 'C (Mixed Grid)',
    color: '#f8d210'
  },
  {
    id: 'eu-central-1',
    name: 'Europe (Frankfurt)',
    country: 'Germany',
    carbonIntensity: 348, // gCO2e / kWh (Renewables + coal/gas)
    renewableShare: 48,
    pue: 1.15,
    rating: 'C (Mixed Grid)',
    color: '#f8d210'
  },
  {
    id: 'us-east-1',
    name: 'US East (N. Virginia)',
    country: 'United States',
    carbonIntensity: 379, // gCO2e / kWh (PJM interconnection - gas/coal)
    renewableShare: 28,
    pue: 1.18,
    rating: 'D+ (Fossil Heavy)',
    color: '#ff7675'
  },
  {
    id: 'ap-southeast-2',
    name: 'Asia Pacific (Sydney)',
    country: 'Australia',
    carbonIntensity: 650, // gCO2e / kWh (Coal heavy)
    renewableShare: 24,
    pue: 1.19,
    rating: 'D (High Carbon)',
    color: '#d63031'
  },
  {
    id: 'ap-south-1',
    name: 'Asia Pacific (Mumbai)',
    country: 'India',
    carbonIntensity: 708, // gCO2e / kWh (Coal heavy)
    renewableShare: 20,
    pue: 1.20,
    rating: 'E (Intensive Carbon)',
    color: '#c0392b'
  }
];

// Hardware Architecture Energy Profiles
export const CPU_ARCHITECTURES = {
  x86: {
    id: 'x86',
    name: 'Legacy x86 (Intel Xeon / AMD EPYC)',
    idleWattsPerVcpu: 2.4,
    activeWattsPerVcpu: 8.2,
    embodiedKgPerVcpu: 19.5, // Manufacturing carbon
    efficiencyNote: 'Higher thermal dissipation and electricity demand'
  },
  graviton: {
    id: 'graviton',
    name: 'AWS Graviton (ARM64 Architecture)',
    idleWattsPerVcpu: 1.0,
    activeWattsPerVcpu: 3.3, // ~60% reduction in electricity demand
    embodiedKgPerVcpu: 14.0,
    efficiencyNote: 'ARM Neoverse cores designed for high compute density and ultra-low power'
  }
};

// Component Power Parameters
export const HARDWARE_POWER_SPECS = {
  ram_watts_per_gb: 0.38, // 0.38W per GB of active RAM
  storage_ssd_watts_per_tb: 1.2, // SSD active power draw
  storage_hdd_watts_per_tb: 6.5, // Spinning disk power draw
  network_watts_per_gb_egress: 0.001 // Core router transit energy
};

// Real-world tangible equivalence constants (EPA Greenhouse Gas Equivalencies)
export const CARBON_EQUIVALENTS = {
  kg_co2_per_gasoline_mile: 0.404, // Average passenger vehicle
  kg_co2_per_smartphone_charge: 0.00822, // Full charge
  kg_co2_per_tree_seedling_10yr: 60.0, // 1 tree seedling grown for 10 years sequestering CO2
  kg_co2_per_transatlantic_flight: 986.0 // 1 passenger London to New York
};

// Architectural Archetype Presets
export const GREENOPS_PRESETS = [
  {
    id: 'nordic_cleantech',
    name: 'Nordic Clean Tech Architecture',
    badge: '96% Clean Hydro',
    region: 'eu-north-1',
    architecture: 'graviton',
    vcpus: 16,
    ramGb: 64,
    storageTb: 5,
    storageType: 'ssd',
    avgUtilizationPercent: 45,
    serverLifespanYears: 5,
    environmentType: 'cloud',
    description: 'High-performance Graviton ARM64 stack situated in Stockholm/Helsinki with hydro & nuclear power, minimizing operational footprint.'
  },
  {
    id: 'enterprise_multiregion',
    name: 'Global Enterprise Multi-Region',
    badge: 'Mixed Production',
    region: 'us-east-1',
    architecture: 'x86',
    vcpus: 64,
    ramGb: 256,
    storageTb: 20,
    storageType: 'ssd',
    avgUtilizationPercent: 55,
    serverLifespanYears: 3,
    environmentType: 'cloud',
    description: 'Standard enterprise production cluster in US East (N. Virginia) on x86 compute with high baseline throughput.'
  },
  {
    id: 'ai_bigdata_cluster',
    name: 'AI & Data Lake Training Cluster',
    badge: 'Compute Heavy',
    region: 'eu-central-1',
    architecture: 'x86',
    vcpus: 128,
    ramGb: 512,
    storageTb: 100,
    storageType: 'ssd',
    avgUtilizationPercent: 75,
    serverLifespanYears: 3,
    environmentType: 'cloud',
    description: 'Large data processing and ML pipeline in Frankfurt with intensive CPU load and massive data lake volumes.'
  },
  {
    id: 'legacy_onprem_migration',
    name: 'Legacy On-Premises Migration',
    badge: 'Legacy Baseline',
    region: 'us-east-1',
    architecture: 'x86',
    vcpus: 48,
    ramGb: 192,
    storageTb: 15,
    storageType: 'hdd',
    avgUtilizationPercent: 25, // Typical low on-prem utilization
    serverLifespanYears: 4,
    environmentType: 'onprem', // High PUE (1.65)
    description: 'Traditional private on-prem datacenter with inefficient cooling (PUE 1.65) and spinning HDDs, ready for cloud greenification.'
  }
];

// Calculation Engine
export const calculateGreenOpsMetrics = (config, activeLevers = {}) => {
  const {
    region: regionId = 'eu-north-1',
    architecture: archId = 'graviton',
    vcpus = 16,
    ramGb = 64,
    storageTb = 5,
    storageType = 'ssd',
    avgUtilizationPercent = 50,
    serverLifespanYears = 4,
    environmentType = 'cloud'
  } = config;

  // Lever Modifiers
  const isRegionShiftActive = Boolean(activeLevers.regionShift); // Shift to eu-north-1
  const isGravitonMigrationActive = Boolean(activeLevers.gravitonMigration); // Convert to Graviton
  const isAutoShutdownActive = Boolean(activeLevers.autoShutdown); // Auto-shutdown saves 65% of non-prod time
  const isLifecycleExtendedActive = Boolean(activeLevers.lifecycleExtension); // Extend lifespan to 5 years

  // Effective Region
  const effectiveRegionId = isRegionShiftActive ? 'eu-north-1' : regionId;
  const regionObj = GRID_CARBON_FACTORS.find(r => r.id === effectiveRegionId) || GRID_CARBON_FACTORS[0];
  const gridIntensity = regionObj.carbonIntensity; // gCO2e / kWh
  
  // Datacenter PUE (Cloud vs On-Prem)
  const basePue = environmentType === 'onprem' ? 1.65 : regionObj.pue;

  // Effective CPU Architecture
  const effectiveArchId = isGravitonMigrationActive ? 'graviton' : archId;
  const archObj = CPU_ARCHITECTURES[effectiveArchId] || CPU_ARCHITECTURES.graviton;

  // 1. CPU Power Calculation
  const utilFraction = Math.min(1.0, Math.max(0.05, avgUtilizationPercent / 100));
  const wattsPerVcpu = archObj.idleWattsPerVcpu + utilFraction * (archObj.activeWattsPerVcpu - archObj.idleWattsPerVcpu);
  const totalCpuWatts = vcpus * wattsPerVcpu;

  // 2. RAM Power Calculation
  const totalRamWatts = ramGb * HARDWARE_POWER_SPECS.ram_watts_per_gb;

  // 3. Storage Power Calculation
  const storageWattRate = storageType === 'hdd' ? HARDWARE_POWER_SPECS.storage_hdd_watts_per_tb : HARDWARE_POWER_SPECS.storage_ssd_watts_per_tb;
  const totalStorageWatts = storageTb * storageWattRate;

  // 4. Server Subtotal Watts & PUE Scaling
  const serverBaseWatts = totalCpuWatts + totalRamWatts + totalStorageWatts;
  const facilityTotalWatts = serverBaseWatts * basePue;

  // 5. Operating Hours & Auto-Shutdown
  // 730 hours in average month. If auto-shutdown is active (10h/day, 5d/week = 216h/mo), hours drop significantly.
  const operatingHoursPerMonth = isAutoShutdownActive ? 260 : 730;

  // 6. Electricity Consumption (kWh / Month)
  const monthlyKwh = (facilityTotalWatts * operatingHoursPerMonth) / 1000;
  const annualKwh = monthlyKwh * 12;

  // 7. Scope 2 Operational Carbon Emissions (kg CO2e)
  // (kWh * gCO2e/kWh) / 1000 = kg CO2e
  const monthlyOperationalKgCo2e = (monthlyKwh * gridIntensity) / 1000;
  const annualOperationalKgCo2e = monthlyOperationalKgCo2e * 12;

  // 8. Scope 3 Embodied Carbon (Manufacturing & Cradle-to-Gate)
  const effectiveLifespanYears = isLifecycleExtendedActive ? 5 : Math.max(3, Number(serverLifespanYears) || 4);
  const totalEmbodiedKgCo2e = vcpus * archObj.embodiedKgPerVcpu;
  const annualEmbodiedKgCo2e = totalEmbodiedKgCo2e / effectiveLifespanYears;
  const monthlyEmbodiedKgCo2e = annualEmbodiedKgCo2e / 12;

  // 9. Total Carbon Footprint (Scope 2 Operational + Scope 3 Embodied)
  const totalMonthlyKgCo2e = monthlyOperationalKgCo2e + monthlyEmbodiedKgCo2e;
  const totalAnnualKgCo2e = annualOperationalKgCo2e + annualEmbodiedKgCo2e;
  const totalAnnualMetricTons = totalAnnualKgCo2e / 1000;

  // 10. Baseline (Un-optimized) Comparison to compute Avoided Carbon
  const unoptimizedRegion = GRID_CARBON_FACTORS.find(r => r.id === regionId) || GRID_CARBON_FACTORS[5]; // fallback us-east-1
  const unoptimizedArch = CPU_ARCHITECTURES[archId] || CPU_ARCHITECTURES.x86;
  const unoptimizedPue = environmentType === 'onprem' ? 1.65 : unoptimizedRegion.pue;
  const unoptWattsPerVcpu = unoptimizedArch.idleWattsPerVcpu + utilFraction * (unoptimizedArch.activeWattsPerVcpu - unoptimizedArch.idleWattsPerVcpu);
  const unoptFacilityWatts = (vcpus * unoptWattsPerVcpu + totalRamWatts + totalStorageWatts) * unoptimizedPue;
  const unoptMonthlyKwh = (unoptFacilityWatts * 730) / 1000;
  const unoptMonthlyScope2 = (unoptMonthlyKwh * unoptimizedRegion.carbonIntensity) / 1000;
  const unoptMonthlyScope3 = (vcpus * unoptimizedArch.embodiedKgPerVcpu) / (Number(serverLifespanYears) || 4) / 12;
  const baselineMonthlyKgCo2e = unoptMonthlyScope2 + unoptMonthlyScope3;
  const baselineAnnualKgCo2e = baselineMonthlyKgCo2e * 12;

  const monthlyCarbonAvoidedKg = Math.max(0, baselineMonthlyKgCo2e - totalMonthlyKgCo2e);
  const annualCarbonAvoidedKg = monthlyCarbonAvoidedKg * 12;
  const reductionPercentage = baselineAnnualKgCo2e > 0 ? (annualCarbonAvoidedKg / baselineAnnualKgCo2e) * 100 : 0;

  // 11. Tangible Real-World Carbon Equivalences
  const milesDrivenAvoided = (annualCarbonAvoidedKg > 0 ? annualCarbonAvoidedKg : totalAnnualKgCo2e) / CARBON_EQUIVALENTS.kg_co2_per_gasoline_mile;
  const treeSeedlingsNeeded = totalAnnualKgCo2e / CARBON_EQUIVALENTS.kg_co2_per_tree_seedling_10yr;
  const smartphonesCharged = totalAnnualKgCo2e / CARBON_EQUIVALENTS.kg_co2_per_smartphone_charge;
  const flightsEquivalent = totalAnnualKgCo2e / CARBON_EQUIVALENTS.kg_co2_per_transatlantic_flight;

  // 12. Multi-Region Comparison Matrix for Charting
  const regionalComparison = GRID_CARBON_FACTORS.map(reg => {
    const regPue = environmentType === 'onprem' ? 1.65 : reg.pue;
    const regKwh = (serverBaseWatts * regPue * operatingHoursPerMonth) / 1000;
    const regScope2Monthly = (regKwh * reg.carbonIntensity) / 1000;
    const regTotalAnnualKg = (regScope2Monthly + monthlyEmbodiedKgCo2e) * 12;
    return {
      id: reg.id,
      name: reg.name,
      country: reg.country,
      carbonIntensity: reg.carbonIntensity,
      annualKgCo2e: regTotalAnnualKg,
      annualTons: regTotalAnnualKg / 1000,
      isCurrent: reg.id === effectiveRegionId,
      rating: reg.rating,
      color: reg.color
    };
  });

  return {
    region: regionObj,
    architecture: archObj,
    pue: basePue,
    operatingHoursPerMonth,
    powerDraw: {
      cpuWatts: totalCpuWatts,
      ramWatts: totalRamWatts,
      storageWatts: totalStorageWatts,
      serverBaseWatts,
      facilityTotalWatts
    },
    energy: {
      monthlyKwh,
      annualKwh
    },
    emissions: {
      monthlyOperationalKgCo2e,
      annualOperationalKgCo2e,
      monthlyEmbodiedKgCo2e,
      annualEmbodiedKgCo2e,
      totalMonthlyKgCo2e,
      totalAnnualKgCo2e,
      totalAnnualMetricTons
    },
    benchmarks: {
      baselineAnnualKgCo2e,
      annualCarbonAvoidedKg,
      reductionPercentage
    },
    equivalents: {
      milesDrivenAvoided,
      treeSeedlingsNeeded,
      smartphonesCharged,
      flightsEquivalent
    },
    regionalComparison
  };
};

// -------------------------------------------------------------
// Calendar Months & Seasonal Variation Model (January - December)
// -------------------------------------------------------------
export const CALENDAR_MONTHS = [
  { id: 'jan', name: 'January', short: 'Jan', season: 'Winter', heatingWeight: 1.45, coolingWeight: 0.1, travelWeight: 0.75 },
  { id: 'feb', name: 'February', short: 'Feb', season: 'Winter', heatingWeight: 1.40, coolingWeight: 0.1, travelWeight: 0.85 },
  { id: 'mar', name: 'March', short: 'Mar', season: 'Spring', heatingWeight: 1.15, coolingWeight: 0.2, travelWeight: 1.00 },
  { id: 'apr', name: 'April', short: 'Apr', season: 'Spring', heatingWeight: 0.85, coolingWeight: 0.4, travelWeight: 1.05 },
  { id: 'may', name: 'May', short: 'May', season: 'Spring', heatingWeight: 0.50, coolingWeight: 0.7, travelWeight: 1.10 },
  { id: 'jun', name: 'June', short: 'Jun', season: 'Summer', heatingWeight: 0.10, coolingWeight: 1.3, travelWeight: 1.20 },
  { id: 'jul', name: 'July', short: 'Jul', season: 'Summer', heatingWeight: 0.05, coolingWeight: 1.5, travelWeight: 1.25 },
  { id: 'aug', name: 'August', short: 'Aug', season: 'Summer', heatingWeight: 0.05, coolingWeight: 1.4, travelWeight: 1.20 },
  { id: 'sep', name: 'September', short: 'Sep', season: 'Autumn', heatingWeight: 0.45, coolingWeight: 0.8, travelWeight: 1.10 },
  { id: 'oct', name: 'October', short: 'Oct', season: 'Autumn', heatingWeight: 0.85, coolingWeight: 0.4, travelWeight: 1.05 },
  { id: 'nov', name: 'November', short: 'Nov', season: 'Winter', heatingWeight: 1.25, coolingWeight: 0.1, travelWeight: 0.95 },
  { id: 'dec', name: 'December', short: 'Dec', season: 'Winter', heatingWeight: 1.50, coolingWeight: 0.1, travelWeight: 0.85 }
];

// Heating Source Emission Factors (kg CO2e / kWh or equivalent)
export const HEATING_FACTORS = {
  heat_pump: { id: 'heat_pump', name: 'Air / Ground Heat Pump (COP 3.5)', factor: 0.025, desc: 'High-efficiency electrified heat pump' },
  district_nordic: { id: 'district_nordic', name: 'Nordic District Heating (Biomass / Waste)', factor: 0.055, desc: 'Decarbonized municipal district heating' },
  natural_gas: { id: 'natural_gas', name: 'Natural Gas Boiler', factor: 0.202, desc: 'Direct fossil fuel combustion' },
  oil_furnace: { id: 'oil_furnace', name: 'Heating Oil Furnace', factor: 0.268, desc: 'Heavy fossil fuel heating' },
  electric_baseboard: { id: 'electric_baseboard', name: 'Direct Electric Baseboard', factor: 0.180, desc: 'Direct resistive electric heating' }
};

// Transport Emission Factors (kg CO2e / km)
export const TRANSPORT_FACTORS = {
  petrol_car: 0.170, // Average gasoline passenger car
  diesel_car: 0.165,
  hybrid_car: 0.105,
  ev_car: 0.035, // Average EV on European grid mix
  train_transit: 0.032, // Rail / metro per passenger-km
  domestic_flight_km: 0.245, // Flight with Radiative Forcing (<1,000 km)
  longhaul_flight_km: 0.150 // Long-haul flight per passenger-km (>1,000 km)
};

// -------------------------------------------------------------
// Individual Presets
// -------------------------------------------------------------
export const INDIVIDUAL_PRESETS = [
  {
    id: 'remote_dev_nordic',
    name: 'Remote Software Engineer (Nordic Clean Tech)',
    badge: 'Clean Grid & Remote',
    region: 'eu-north-1',
    homeElectricityKwhMonthly: 280,
    greenElectricityTariff: true,
    heatingType: 'heat_pump',
    homeAreaSqM: 85,
    heatingKwhMonthlyBase: 350,
    laptopsCount: 1,
    monitorsCount: 2,
    deviceLifespanYears: 4,
    cloudMonthlyKwh: 45, // Dev VMs & API inference
    carType: 'ev_car',
    carAnnualKm: 4000,
    trainAnnualKm: 3500,
    domesticFlightsCount: 1,
    longhaulFlightsCount: 0,
    desc: 'Fully remote software engineer living in Helsinki/Stockholm using a heat pump, renewable grid tariff, EV/train mobility, and cloud workloads.'
  },
  {
    id: 'hybrid_consultant_eu',
    name: 'Hybrid Tech Consultant (EU Central)',
    badge: 'Hybrid & Rail',
    region: 'eu-central-1',
    homeElectricityKwhMonthly: 340,
    greenElectricityTariff: false,
    heatingType: 'district_nordic',
    homeAreaSqM: 110,
    heatingKwhMonthlyBase: 500,
    laptopsCount: 2,
    monitorsCount: 2,
    deviceLifespanYears: 3,
    cloudMonthlyKwh: 80,
    carType: 'hybrid_car',
    carAnnualKm: 9000,
    trainAnnualKm: 6000,
    domesticFlightsCount: 3,
    longhaulFlightsCount: 1,
    desc: 'Hybrid technology architect based in Frankfurt/Berlin commuting via rail and hybrid vehicle with moderate regional consulting travel.'
  },
  {
    id: 'us_highflyer_consultant',
    name: 'Senior Systems Architect (US East / High Travel)',
    badge: 'Fossil Grid & High Travel',
    region: 'us-east-1',
    homeElectricityKwhMonthly: 550,
    greenElectricityTariff: false,
    heatingType: 'natural_gas',
    homeAreaSqM: 180,
    heatingKwhMonthlyBase: 800,
    laptopsCount: 2,
    monitorsCount: 3,
    deviceLifespanYears: 3,
    cloudMonthlyKwh: 140,
    carType: 'petrol_car',
    carAnnualKm: 16000,
    trainAnnualKm: 800,
    domesticFlightsCount: 8,
    longhaulFlightsCount: 2,
    desc: 'US East professional with substantial suburban commuting, natural gas heating, high baseline home power, and frequent interstate business flights.'
  }
];

// -------------------------------------------------------------
// Medium-Sized Company Presets (20 to 250 Employees)
// -------------------------------------------------------------
export const COMPANY_PRESETS = [
  {
    id: 'remote_tech_startup',
    name: 'Green-First Remote Software Corp (45 FTEs)',
    badge: '100% Remote / Cloud-Native',
    employeesCount: 45,
    remoteWorkPercent: 85, // 85% remote, 15% co-working
    officeSqM: 250, // Small co-working / hub
    region: 'eu-north-1',
    greenTariff: true,
    annualOfficeElectricityKwh: 18000,
    heatingType: 'heat_pump',
    annualOfficeHeatingKwh: 12000,
    fleetCount: 0,
    fleetType: 'ev_car',
    fleetAnnualKmTotal: 0,
    annualDomesticFlights: 15, // Annual company retreat
    annualLonghaulFlights: 4,
    annualHotelNights: 60,
    cloudMonthlySpendEst: 4500, // Multi-cloud infrastructure
    cloudInstancesCount: 35,
    onpremServerRacks: 0,
    desc: 'Modern cloud-native software firm with an 85% remote distributed team, co-working hub in the Nordics, zero fleet, and optimized cloud architecture.'
  },
  {
    id: 'hybrid_saas_smb',
    name: 'Mid-Sized SaaS & Engineering Enterprise (120 FTEs)',
    badge: 'Hybrid / 120 Staff',
    employeesCount: 120,
    remoteWorkPercent: 40, // 40% remote, 60% in-office hybrid
    officeSqM: 1400,
    region: 'eu-west-1',
    greenTariff: false,
    annualOfficeElectricityKwh: 115000,
    heatingType: 'district_nordic',
    annualOfficeHeatingKwh: 95000,
    fleetCount: 8,
    fleetType: 'hybrid_car',
    fleetAnnualKmTotal: 96000,
    annualDomesticFlights: 65,
    annualLonghaulFlights: 18,
    annualHotelNights: 280,
    cloudMonthlySpendEst: 18500,
    cloudInstancesCount: 140,
    onpremServerRacks: 2,
    desc: 'Mature mid-market SaaS enterprise operating a hybrid office facility in Western Europe with moderate sales travel and hybrid company fleet.'
  },
  {
    id: 'consulting_services_smb',
    name: 'Global Tech Advisory & Professional Services (220 FTEs)',
    badge: 'In-Office & High Flight / 220 Staff',
    employeesCount: 220,
    remoteWorkPercent: 20, // 80% on-site / office
    officeSqM: 3200,
    region: 'us-east-1',
    greenTariff: false,
    annualOfficeElectricityKwh: 340000,
    heatingType: 'natural_gas',
    annualOfficeHeatingKwh: 260000,
    fleetCount: 25,
    fleetType: 'petrol_car',
    fleetAnnualKmTotal: 350000,
    annualDomesticFlights: 220,
    annualLonghaulFlights: 60,
    annualHotelNights: 950,
    cloudMonthlySpendEst: 38000,
    cloudInstancesCount: 280,
    onpremServerRacks: 6,
    desc: 'Professional technology consulting practice with headquarters in US East, widespread executive travel, significant on-prem lab racks, and large office facilities.'
  }
];

// -------------------------------------------------------------
// Individual Annual Emission Engine (January - December)
// -------------------------------------------------------------
export const calculateIndividualAnnualEmissions = (config) => {
  const regionObj = GRID_CARBON_FACTORS.find(r => r.id === config.region) || GRID_CARBON_FACTORS[0];
  const gridFactor = config.greenElectricityTariff ? 15 : regionObj.carbonIntensity; // gCO2e / kWh
  const gridKgPerKwh = gridFactor / 1000;

  // 1. Home Electricity (Scope 2)
  const monthlyBaseKwh = Number(config.homeElectricityKwhMonthly) || 300;
  
  // 2. Space Heating (Scope 1 if gas/oil, Scope 2 if heat pump / electric)
  const heatingObj = HEATING_FACTORS[config.heatingType] || HEATING_FACTORS.heat_pump;
  const heatingBaseKwh = Number(config.heatingKwhMonthlyBase) || 400;
  const isDirectScope1Heating = config.heatingType === 'natural_gas' || config.heatingType === 'oil_furnace';

  // 3. Hardware Embodied Carbon (Scope 3)
  const lifespan = Math.max(2, Number(config.deviceLifespanYears) || 3);
  const laptopEmbodiedKgTotal = (Number(config.laptopsCount) || 1) * 250;
  const monitorEmbodiedKgTotal = (Number(config.monitorsCount) || 1) * 200;
  const annualHardwareEmbodiedKg = (laptopEmbodiedKgTotal + monitorEmbodiedKgTotal) / lifespan;

  // 4. Cloud & Digital Services (Scope 3)
  const cloudMonthlyKwh = Number(config.cloudMonthlyKwh) || 50;
  const annualCloudKg = cloudMonthlyKwh * 12 * gridKgPerKwh;

  // 5. Mobility & Travel
  const carFuelFactor = TRANSPORT_FACTORS[config.carType] || TRANSPORT_FACTORS.hybrid_car;
  const annualCarKg = (Number(config.carAnnualKm) || 0) * carFuelFactor;
  const isCarScope1 = config.carType !== 'ev_car';

  const annualTrainKg = (Number(config.trainAnnualKm) || 0) * TRANSPORT_FACTORS.train_transit;
  const annualDomesticFlightsKg = (Number(config.domesticFlightsCount) || 0) * 800 * TRANSPORT_FACTORS.domestic_flight_km; // avg 800 km domestic
  const annualLonghaulFlightsKg = (Number(config.longhaulFlightsCount) || 0) * 6500 * TRANSPORT_FACTORS.longhaul_flight_km; // avg 6,500 km long-haul
  const annualFlightsKg = annualDomesticFlightsKg + annualLonghaulFlightsKg;

  // 6. Generate 12-Month January to December Trajectory
  let annualScope1Kg = 0;
  let annualScope2Kg = 0;
  let annualScope3Kg = 0;

  const monthlyTrajectory = CALENDAR_MONTHS.map(month => {
    // Electricity varies with cooling/lighting
    const monthElectricityKwh = monthlyBaseKwh * (0.85 + month.coolingWeight * 0.25);
    const monthElectricityKg = monthElectricityKwh * gridKgPerKwh;

    // Heating varies with winter curve
    const monthHeatingKwh = heatingBaseKwh * month.heatingWeight;
    const monthHeatingKg = monthHeatingKwh * heatingObj.factor;

    // Transport distributed across months with summer/travel weight
    const monthCarKg = (annualCarKg / 12) * month.travelWeight;
    const monthTrainKg = (annualTrainKg / 12) * month.travelWeight;
    const monthFlightsKg = (annualFlightsKg / 12) * month.travelWeight;

    // Hardware & Digital constant per month
    const monthHardwareKg = annualHardwareEmbodiedKg / 12;
    const monthCloudKg = annualCloudKg / 12;

    // Scope Assignment
    let monthScope1 = isCarScope1 ? monthCarKg : 0;
    if (isDirectScope1Heating) {
      monthScope1 += monthHeatingKg;
    }

    let monthScope2 = monthElectricityKg;
    if (!isDirectScope1Heating) {
      monthScope2 += monthHeatingKg;
    }
    if (config.carType === 'ev_car') {
      monthScope2 += monthCarKg; // EV charging on domestic grid
    }

    const monthScope3 = monthTrainKg + monthFlightsKg + monthHardwareKg + monthCloudKg;
    const monthTotalKg = monthScope1 + monthScope2 + monthScope3;

    annualScope1Kg += monthScope1;
    annualScope2Kg += monthScope2;
    annualScope3Kg += monthScope3;

    return {
      id: month.id,
      name: month.name,
      short: month.short,
      season: month.season,
      electricityKwh: monthElectricityKwh,
      electricityKg: monthElectricityKg,
      heatingKg: monthHeatingKg,
      carKg: monthCarKg,
      transitAndFlightsKg: monthTrainKg + monthFlightsKg,
      digitalAndHardwareKg: monthHardwareKg + monthCloudKg,
      scope1: monthScope1,
      scope2: monthScope2,
      scope3: monthScope3,
      totalKg: monthTotalKg,
      totalTons: monthTotalKg / 1000
    };
  });

  const totalAnnualKg = annualScope1Kg + annualScope2Kg + annualScope3Kg;
  const totalAnnualTons = totalAnnualKg / 1000;
  const dailyAverageKg = totalAnnualKg / 365;

  // Paris Agreement Benchmark Target: ~2.0 Metric Tons / year per capita by 2030
  const euAveragePerCapitaTons = 6.8; // Average EU citizen ~6.8 tCO2e
  const varianceFromTarget = ((totalAnnualTons - 2.0) / 2.0) * 100;

  return {
    region: regionObj,
    gridFactor,
    scopes: {
      scope1Kg: annualScope1Kg,
      scope2Kg: annualScope2Kg,
      scope3Kg: annualScope3Kg,
      scope1Tons: annualScope1Kg / 1000,
      scope2Tons: annualScope2Kg / 1000,
      scope3Tons: annualScope3Kg / 1000,
      scope1Share: totalAnnualKg > 0 ? (annualScope1Kg / totalAnnualKg) * 100 : 0,
      scope2Share: totalAnnualKg > 0 ? (annualScope2Kg / totalAnnualKg) * 100 : 0,
      scope3Share: totalAnnualKg > 0 ? (annualScope3Kg / totalAnnualKg) * 100 : 0
    },
    totals: {
      totalAnnualKg,
      totalAnnualTons,
      dailyAverageKg,
      euAveragePerCapitaTons,
      varianceFromTarget
    },
    breakdownByCategory: {
      homeElectricityKg: monthlyBaseKwh * 12 * gridKgPerKwh,
      homeHeatingKg: heatingBaseKwh * 12 * heatingObj.factor,
      carTransportKg: annualCarKg,
      flightsKg: annualFlightsKg,
      trainKg: annualTrainKg,
      hardwareEmbodiedKg: annualHardwareEmbodiedKg,
      cloudDigitalKg: annualCloudKg
    },
    monthlyTrajectory
  };
};

// -------------------------------------------------------------
// Medium-Sized Company Annual Emission Engine (January - December)
// -------------------------------------------------------------
export const calculateCompanyAnnualEmissions = (config) => {
  const regionObj = GRID_CARBON_FACTORS.find(r => r.id === config.region) || GRID_CARBON_FACTORS[0];
  const gridFactor = config.greenTariff ? 20 : regionObj.carbonIntensity;
  const gridKgPerKwh = gridFactor / 1000;

  const fte = Math.max(10, Number(config.employeesCount) || 50);
  const officeSqM = Number(config.officeSqM) || 800;
  const remotePercent = Math.min(100, Math.max(0, Number(config.remoteWorkPercent) || 50));
  const inOfficePercent = 100 - remotePercent;

  // 1. Office Electricity & HVAC (Scope 2)
  const annualOfficeElecKwh = Number(config.annualOfficeElectricityKwh) || officeSqM * 90;
  const annualOfficeElecKg = annualOfficeElecKwh * gridKgPerKwh;

  // 2. Office Heating (Scope 1 for Gas/Oil, Scope 2 for District/Pump)
  const heatingObj = HEATING_FACTORS[config.heatingType] || HEATING_FACTORS.district_nordic;
  const annualOfficeHeatingKwh = Number(config.annualOfficeHeatingKwh) || officeSqM * 75;
  const annualOfficeHeatingKg = annualOfficeHeatingKwh * heatingObj.factor;
  const isOfficeHeatingScope1 = config.heatingType === 'natural_gas' || config.heatingType === 'oil_furnace';

  // 3. Corporate Fleet (Scope 1)
  const fleetKm = Number(config.fleetAnnualKmTotal) || 0;
  const fleetFactor = TRANSPORT_FACTORS[config.fleetType] || TRANSPORT_FACTORS.hybrid_car;
  const annualFleetKg = fleetKm * fleetFactor;
  const isFleetScope1 = config.fleetType !== 'ev_car';

  // 4. Business Travel (Scope 3)
  const domFlightKm = (Number(config.annualDomesticFlights) || 0) * 850;
  const longFlightKm = (Number(config.annualLonghaulFlights) || 0) * 6800;
  const annualFlightKg = domFlightKm * TRANSPORT_FACTORS.domestic_flight_km + longFlightKm * TRANSPORT_FACTORS.longhaul_flight_km;
  const hotelKg = (Number(config.annualHotelNights) || 0) * 28.0; // 28 kg CO2e per hotel room-night

  // 5. Cloud Computing & On-Prem Racks (Scope 3 / Scope 2)
  const instances = Number(config.cloudInstancesCount) || 50;
  const annualCloudComputeKg = instances * 16 * 0.005 * 730 * 12 * gridKgPerKwh; // ~16 vCPU average
  const onpremRacks = Number(config.onpremServerRacks) || 0;
  const annualOnpremElecKwh = onpremRacks * 2.5 * 730 * 12; // 2.5kW per rack
  const annualOnpremKg = annualOnpremElecKwh * gridKgPerKwh;

  // 6. Employee Remote Working & Hardware Provisioning (Scope 3)
  // Estimated 1.5 kWh/day per remote worker for workstation + lighting
  const remoteFte = fte * (remotePercent / 100);
  const annualRemoteEnergyKg = remoteFte * 220 * 1.8 * 0.250; // 220 working days, 1.8 kWh/day, average home grid mix
  const annualHardwareProvisioningKg = fte * (250 / 3); // 1 laptop per employee on 3-year refresh cycle

  // 7. Supply Chain & Purchased Goods Baseline (Scope 3)
  const operationalProcurementKg = (annualOfficeElecKg + annualFlightKg) * 0.15; // 15% procurement buffer

  // Synthesize 12-Month January to December Trajectory
  let annualScope1Kg = 0;
  let annualScope2Kg = 0;
  let annualScope3Kg = 0;

  const monthlyTrajectory = CALENDAR_MONTHS.map(month => {
    const monthElecKg = (annualOfficeElecKg / 12) * (0.85 + month.coolingWeight * 0.25);
    const monthHeatingKg = (annualOfficeHeatingKg / 12) * month.heatingWeight;
    const monthFleetKg = (annualFleetKg / 12) * month.travelWeight;
    const monthTravelKg = ((annualFlightKg + hotelKg) / 12) * month.travelWeight;
    const monthCloudKg = annualCloudComputeKg / 12;
    const monthOnpremKg = annualOnpremKg / 12;
    const monthRemoteWorkKg = annualRemoteEnergyKg / 12;
    const monthHardwareKg = annualHardwareProvisioningKg / 12;
    const monthProcurementKg = operationalProcurementKg / 12;

    let mScope1 = isFleetScope1 ? monthFleetKg : 0;
    if (isOfficeHeatingScope1) mScope1 += monthHeatingKg;

    let mScope2 = monthElecKg + monthOnpremKg;
    if (!isOfficeHeatingScope1) mScope2 += monthHeatingKg;
    if (!isFleetScope1) mScope2 += monthFleetKg; // EV charging on company premises

    const mScope3 = monthTravelKg + monthCloudKg + monthRemoteWorkKg + monthHardwareKg + monthProcurementKg;
    const mTotalKg = mScope1 + mScope2 + mScope3;

    annualScope1Kg += mScope1;
    annualScope2Kg += mScope2;
    annualScope3Kg += mScope3;

    return {
      id: month.id,
      name: month.name,
      short: month.short,
      season: month.season,
      officeElectricityKg: monthElecKg,
      officeHeatingKg: monthHeatingKg,
      fleetKg: monthFleetKg,
      businessTravelKg: monthTravelKg,
      cloudAndItKg: monthCloudKg + monthOnpremKg,
      remoteWorkAndProcurementKg: monthRemoteWorkKg + monthHardwareKg + monthProcurementKg,
      scope1: mScope1,
      scope2: mScope2,
      scope3: mScope3,
      totalKg: mTotalKg,
      totalTons: mTotalKg / 1000
    };
  });

  const totalAnnualKg = annualScope1Kg + annualScope2Kg + annualScope3Kg;
  const totalAnnualTons = totalAnnualKg / 1000;
  const intensityPerFteTons = totalAnnualTons / fte;
  const intensityPerSqMTons = totalAnnualTons / officeSqM;

  return {
    fte,
    officeSqM,
    remotePercent,
    inOfficePercent,
    region: regionObj,
    gridFactor,
    scopes: {
      scope1Kg: annualScope1Kg,
      scope2Kg: annualScope2Kg,
      scope3Kg: annualScope3Kg,
      scope1Tons: annualScope1Kg / 1000,
      scope2Tons: annualScope2Kg / 1000,
      scope3Tons: annualScope3Kg / 1000,
      scope1Share: totalAnnualKg > 0 ? (annualScope1Kg / totalAnnualKg) * 100 : 0,
      scope2Share: totalAnnualKg > 0 ? (annualScope2Kg / totalAnnualKg) * 100 : 0,
      scope3Share: totalAnnualKg > 0 ? (annualScope3Kg / totalAnnualKg) * 100 : 0
    },
    totals: {
      totalAnnualKg,
      totalAnnualTons,
      intensityPerFteTons,
      intensityPerSqMTons,
      smeBenchmarkPerFteTons: 4.5 // Typical EU tech SME benchmark ~4.5 tCO2e/employee
    },
    breakdownByCategory: {
      facilitiesElectricityKg: annualOfficeElecKg,
      facilitiesHeatingKg: annualOfficeHeatingKg,
      corporateFleetKg: annualFleetKg,
      businessTravelAndHotelsKg: annualFlightKg + hotelKg,
      cloudAndDataCentersKg: annualCloudComputeKg + annualOnpremKg,
      remoteWorkEnergyKg: annualRemoteEnergyKg,
      hardwareAndProcurementKg: annualHardwareProvisioningKg + operationalProcurementKg
    },
    monthlyTrajectory
  };
};

// -------------------------------------------------------------
// Report Synthesizers
// -------------------------------------------------------------

// Individual Comprehensive Report
export const generateIndividualAnnualReport = (config, report) => {
  const date = new Date().toISOString().split('T')[0];
  return `# Individual Annual Carbon Footprint Audit Report (January – December)
**Auditee / Profile**: Individual Technology Professional & Remote Specialist
**Reporting Standard**: GHG Protocol Corporate & Scope 3 Standard | ISO 14064-1
**Reporting Period**: January 1 – December 31 (Annual Calendar Cycle)
**Assessment Date**: ${date}
**Selected Location / Grid**: ${report.region.name} (${report.region.country})
**Grid Emission Factor**: ${report.gridFactor} gCO₂e / kWh ${config.greenElectricityTariff ? '(Green Tariff Enabled)' : ''}

---

## 1. Executive Summary & Greenhouse Gas (GHG) Breakdown

| Emission Scope | Annual Emissions (kg CO₂e) | Metric Tons (tCO₂e) | Share of Footprint |
| :--- | :--- | :--- | :--- |
| **Scope 1: Direct Emissions** (Heating fuel & car combustion) | ${report.scopes.scope1Kg.toFixed(1)} kg | **${report.scopes.scope1Tons.toFixed(2)} tCO₂e** | ${report.scopes.scope1Share.toFixed(1)}% |
| **Scope 2: Indirect Emissions** (Purchased residential electricity) | ${report.scopes.scope2Kg.toFixed(1)} kg | **${report.scopes.scope2Tons.toFixed(2)} tCO₂e** | ${report.scopes.scope2Share.toFixed(1)}% |
| **Scope 3: Value Chain Emissions** (Flights, rail, hardware, cloud APIs) | ${report.scopes.scope3Kg.toFixed(1)} kg | **${report.scopes.scope3Tons.toFixed(2)} tCO₂e** | ${report.scopes.scope3Share.toFixed(1)}% |
| **TOTAL ANNUAL CARBON FOOTPRINT** | **${report.totals.totalAnnualKg.toFixed(1)} kg** | **${report.totals.totalAnnualTons.toFixed(2)} Metric Tons CO₂e** | **100.0%** |

### Benchmark Analysis:
- **Daily Average Emissions**: \`${report.totals.dailyAverageKg.toFixed(2)} kg CO₂e / day\`
- **Comparison to EU Average**: Current footprint is **${report.totals.totalAnnualTons.toFixed(2)} tCO₂e** vs. EU per-capita baseline of **${report.totals.euAveragePerCapitaTons} tCO₂e** (${((report.totals.totalAnnualTons / report.totals.euAveragePerCapitaTons) * 100).toFixed(1)}% of EU average).
- **Paris Agreement 2030 Target (2.0 tCO₂e)**: \`${report.totals.varianceFromTarget > 0 ? '+' : ''}${report.totals.varianceFromTarget.toFixed(1)}%\` variance from target.

---

## 2. January – December Monthly Trajectory (Calendar Breakdown)

| Month | Season | Electricity (kg) | Heating (kg) | Transport (kg) | Digital / HW (kg) | Total (kg CO₂e) | Metric Tons |
| :--- | :--- | :--- | :--- | :--- | :--- | :--- | :--- |
${report.monthlyTrajectory.map(m => `| **${m.name}** | ${m.season} | ${m.electricityKg.toFixed(1)} | ${m.heatingKg.toFixed(1)} | ${(m.carKg + m.transitAndFlightsKg).toFixed(1)} | ${m.digitalAndHardwareKg.toFixed(1)} | **${m.totalKg.toFixed(1)}** | **${m.totalTons.toFixed(2)}** |`).join('\n')}

---

## 3. Disaggregated Category Line Items

- **Home Office & Domestic Electricity**: \`${report.breakdownByCategory.homeElectricityKg.toFixed(1)} kg CO₂e / year\`
- **Space Heating & Hot Water**: \`${report.breakdownByCategory.homeHeatingKg.toFixed(1)} kg CO₂e / year\`
- **Personal & Commuting Vehicle Travel**: \`${report.breakdownByCategory.carTransportKg.toFixed(1)} kg CO₂e / year\`
- **Domestic & International Aviation**: \`${report.breakdownByCategory.flightsKg.toFixed(1)} kg CO₂e / year\`
- **Public Transit & Rail Mobility**: \`${report.breakdownByCategory.trainKg.toFixed(1)} kg CO₂e / year\`
- **Hardware Manufacturing Amortization (Laptops, Displays)**: \`${report.breakdownByCategory.hardwareEmbodiedKg.toFixed(1)} kg CO₂e / year\`
- **Cloud Computing & AI Inference Services**: \`${report.breakdownByCategory.cloudDigitalKg.toFixed(1)} kg CO₂e / year\`

---

## 4. Science-Based Decarbonization Roadmap

1. **Clean Energy Tariff**: Switch residential contract to certified 100% renewable wind/solar to eliminate Scope 2 electricity emissions.
2. **Mobility Modal Shift**: Substitute short-haul domestic flights with high-speed passenger rail (-80% travel emissions).
3. **Circular Hardware Longevity**: Extend workstation hardware replacement cycle from 3 to 5 years to reduce Scope 3 embodied carbon by 40%.
4. **Cloud GreenOps Sizing**: Host developer staging environments in clean energy regions (\`eu-north-1\`) and schedule auto-shutdown outside work hours.

---
*Report synthesized by Adedeji Babalola's Cloud Carbon & GreenOps Estimator.*
`;
};

// Medium-Sized Company Comprehensive Report
export const generateCompanyAnnualReport = (config, report) => {
  const date = new Date().toISOString().split('T')[0];
  return `# Corporate Annual GHG Emissions & CSRD Audit Report (January – December)
**Entity Profile**: Medium-Sized Enterprise (${report.fte} Full-Time Equivalents)
**Reporting Standard**: GHG Protocol Corporate Accounting Standard | CSRD (ESRS E1 Climate Change) | ISO 14064-1
**Reporting Period**: January 1 – December 31 (Annual Accounting Cycle)
**Audit Date**: ${date}
**Primary Facility Location**: ${report.region.name} (${report.region.country})
**Grid Emission Factor**: ${report.gridFactor} gCO₂e / kWh ${config.greenTariff ? '(Verified Green Tariff)' : ''}
**Workforce Dynamics**: ${report.remotePercent}% Distributed Remote | ${report.inOfficePercent}% Office-Based

---

## 1. Executive Summary & Corporate GHG Protocol Disclosures

| Accounting Scope | Annual Gross Emissions | Share (%) | Intensity / FTE | Intensity / m² |
| :--- | :--- | :--- | :--- | :--- |
| **Scope 1: Direct Combustion** (Company Fleet & On-site Heating) | **${report.scopes.scope1Tons.toFixed(2)} tCO₂e** | ${report.scopes.scope1Share.toFixed(1)}% | ${(report.scopes.scope1Tons / report.fte).toFixed(2)} t | ${(report.scopes.scope1Kg / report.officeSqM).toFixed(1)} kg |
| **Scope 2: Purchased Electricity** (Offices & On-prem Datacenters) | **${report.scopes.scope2Tons.toFixed(2)} tCO₂e** | ${report.scopes.scope2Share.toFixed(1)}% | ${(report.scopes.scope2Tons / report.fte).toFixed(2)} t | ${(report.scopes.scope2Kg / report.officeSqM).toFixed(1)} kg |
| **Scope 3: Value Chain & Operations** (Business Travel, Cloud, Remote Work) | **${report.scopes.scope3Tons.toFixed(2)} tCO₂e** | ${report.scopes.scope3Share.toFixed(1)}% | ${(report.scopes.scope3Tons / report.fte).toFixed(2)} t | ${(report.scopes.scope3Kg / report.officeSqM).toFixed(1)} kg |
| **TOTAL CORPORATE CARBON FOOTPRINT** | **${report.totals.totalAnnualTons.toFixed(2)} Metric Tons CO₂e** | **100.0%** | **${report.totals.intensityPerFteTons.toFixed(2)} tCO₂e / FTE** | **${report.totals.intensityPerSqMTons.toFixed(3)} tCO₂e / m²** |

### Benchmark & Competitiveness:
- **Carbon Intensity per Employee**: \`${report.totals.intensityPerFteTons.toFixed(2)} tCO₂e / FTE\` (Industry SME Benchmark: ~4.5 tCO₂e/FTE).
- **Emissions Performance Index**: ${report.totals.intensityPerFteTons <= 4.5 ? '✅ Outperforming European Tech SME Benchmark' : '⚠️ Above European Tech SME Benchmark - Action Required'}.

---

## 2. January – December Monthly Trajectory (Calendar Breakdown)

| Month | Season | Scope 1 (kg) | Scope 2 (kg) | Scope 3 (kg) | Total (kg CO₂e) | Total (Metric Tons) |
| :--- | :--- | :--- | :--- | :--- | :--- | :--- |
${report.monthlyTrajectory.map(m => `| **${m.name}** | ${m.season} | ${m.scope1.toFixed(1)} | ${m.scope2.toFixed(1)} | ${m.scope3.toFixed(1)} | **${m.totalKg.toFixed(1)}** | **${m.totalTons.toFixed(2)}** |`).join('\n')}

---

## 3. Disaggregated Corporate Emissions by Category

- **Facility Purchased Electricity**: \`${(report.breakdownByCategory.facilitiesElectricityKg / 1000).toFixed(2)} tCO₂e\`
- **Facility Heating & Natural Gas**: \`${(report.breakdownByCategory.facilitiesHeatingKg / 1000).toFixed(2)} tCO₂e\`
- **Corporate Fleet Operations**: \`${(report.breakdownByCategory.corporateFleetKg / 1000).toFixed(2)} tCO₂e\`
- **Commercial Aviation & Hotel Lodging**: \`${(report.breakdownByCategory.businessTravelAndHotelsKg / 1000).toFixed(2)} tCO₂e\`
- **Multi-Cloud Architecture & Internal Datacenters**: \`${(report.breakdownByCategory.cloudAndDataCentersKg / 1000).toFixed(2)} tCO₂e\`
- **Employee Teleworking Energy Allocation**: \`${(report.breakdownByCategory.remoteWorkEnergyKg / 1000).toFixed(2)} tCO₂e\`
- **Hardware Procurement & Supply Chain**: \`${(report.breakdownByCategory.hardwareAndProcurementKg / 1000).toFixed(2)} tCO₂e\`

---

## 4. CSRD / SBTi Net-Zero Decarbonization Action Plan

1. **Facility Electrification & PPA**: Transition commercial offices to certified renewable Power Purchase Agreements (PPAs) or onsite solar arrays to eradicate Scope 2 market-based footprint.
2. **Fleet Modernization**: Electrify 100% of corporate vehicle fleet by Q4 next year.
3. **Cloud GreenOps & Spatial Shifting**: Shift cloud workloads from fossil-heavy regions (US-East / Frankfurt) to hydro/nuclear regions (\`eu-north-1\`) and adopt AWS Graviton ARM64 architecture (-60% compute energy).
4. **Virtual-First Travel Policy**: Introduce travel carbon budgets and enforce high-speed rail for domestic journeys under 4 hours.

---
*Report generated by Adedeji Babalola's Cloud Carbon & GreenOps Estimator (Corporate ESG Edition).*
`;
};

// CSV Monthly Trajectory Exporter
export const generateCsvExport = (trajectory, title) => {
  const headers = ['Month', 'Season', 'Scope 1 (kg CO2e)', 'Scope 2 (kg CO2e)', 'Scope 3 (kg CO2e)', 'Total (kg CO2e)', 'Total (Metric Tons)'];
  const rows = trajectory.map(m => [
    m.name,
    m.season,
    m.scope1.toFixed(2),
    m.scope2.toFixed(2),
    m.scope3.toFixed(2),
    m.totalKg.toFixed(2),
    m.totalTons.toFixed(3)
  ]);

  const csvContent = [
    `# ${title} - January to December Monthly Trajectory`,
    headers.join(','),
    ...rows.map(r => r.join(','))
  ].join('\n');

  return csvContent;
};

// Generate Markdown ESG Audit Report for Cloud Infrastructure
export const generateEsgReport = (config, report, activeLevers) => {
  const date = new Date().toISOString().split('T')[0];
  return `# Corporate Sustainability & GreenOps Cloud Emissions Report
**Organization**: Adedeji Babalola Cloud Engineering Practice
**Reporting Standards**: GHG Protocol (Scope 2 & Scope 3) | Green Software Foundation (SCI)
**Audit Date**: ${date}
**Evaluation Region**: ${report.region.name} (${report.region.id})
**Infrastructure Profile**: ${config.vcpus} vCPUs, ${config.ramGb} GB RAM, ${config.storageTb} TB ${config.storageType.toUpperCase()}
**Hardware Architecture**: ${report.architecture.name}

---

## 1. Executive Summary & Greenhouse Gas (GHG) Breakdown

| Emissions Scope | Monthly Emissions | Annualized Footprint | Share |
| :--- | :--- | :--- | :--- |
| **Scope 2 (Operational Grid Electricity)** | ${report.emissions.monthlyOperationalKgCo2e.toFixed(1)} kg CO₂e | ${(report.emissions.annualOperationalKgCo2e / 1000).toFixed(2)} Metric Tons CO₂e | ${((report.emissions.monthlyOperationalKgCo2e / report.emissions.totalMonthlyKgCo2e) * 100).toFixed(1)}% |
| **Scope 3 (Hardware Embodied Carbon)** | ${report.emissions.monthlyEmbodiedKgCo2e.toFixed(1)} kg CO₂e | ${(report.emissions.annualEmbodiedKgCo2e / 1000).toFixed(2)} Metric Tons CO₂e | ${((report.emissions.monthlyEmbodiedKgCo2e / report.emissions.totalMonthlyKgCo2e) * 100).toFixed(1)}% |
| **Total Cloud Carbon Footprint** | **${report.emissions.totalMonthlyKgCo2e.toFixed(1)} kg CO₂e** | **${report.emissions.totalAnnualMetricTons.toFixed(2)} Metric Tons CO₂e** | **100.0%** |

---

## 2. Energy Efficiency & Power Metrics
- **Datacenter Power Usage Effectiveness (PUE)**: \`${report.pue.toFixed(2)}\`
- **Total Facility Power Draw**: \`${report.powerDraw.facilityTotalWatts.toFixed(1)} W\` (Server: ${report.powerDraw.serverBaseWatts.toFixed(1)} W)
- **Monthly Power Consumption**: \`${report.energy.monthlyKwh.toFixed(1)} kWh/month\`
- **Annual Power Consumption**: \`${report.energy.annualKwh.toFixed(1)} kWh/year\`
- **Regional Electricity Grid Factor**: \`${report.region.carbonIntensity} gCO₂e / kWh\` (${report.region.rating})

---

## 3. Circular Economy & Net-Zero Avoided Emissions
- **Baseline Comparative Footprint**: \`${(report.benchmarks.baselineAnnualKgCo2e / 1000).toFixed(2)} Metric Tons CO₂e / year\`
- **Annual Carbon Emissions Avoided**: \`${(report.benchmarks.annualCarbonAvoidedKg / 1000).toFixed(2)} Metric Tons CO₂e / year\`
- **Net Emissions Reduction Rate**: \`${report.benchmarks.reductionPercentage.toFixed(1)}%\`

### Active GreenOps Levers Implemented:
${activeLevers.regionShift ? '- [x] **Region Shifting**: Workload migrated to clean Nordic hydro/nuclear grid (`eu-north-1`).' : '- [ ] Region Shifting: Running in local grid.'}
${activeLevers.gravitonMigration ? '- [x] **Architecture Modernization**: Upgraded to high-efficiency AWS Graviton ARM64 architecture (-60% compute energy).' : '- [ ] Architecture Modernization: Legacy x86 compute.'}
${activeLevers.autoShutdown ? '- [x] **Dynamic Rightsizing & Auto-Shutdown**: Non-production environments scheduled to power down during non-business hours.' : '- [ ] Dynamic Rightsizing: Continuous 24/7 runtime.'}
${activeLevers.lifecycleExtension ? '- [x] **Hardware Life Extension**: Amortized embodied carbon over 5-year hardware circular lifecycle.' : '- [ ] Hardware Life Extension: Standard 3-year replacement cycle.'}

---

## 4. Real-World Environmental Equivalences
- **Tree Seedlings Grown for 10 Years**: Equivalent to planting \`${Math.round(report.equivalents.treeSeedlingsNeeded)}\` trees.
- **Passenger Vehicle Travel**: Equivalent to \`${Math.round(report.equivalents.milesDrivenAvoided).toLocaleString()}\` gasoline car miles avoided.
- **Smartphones Charged**: Equivalent to \`${Math.round(report.equivalents.smartphonesCharged).toLocaleString()}\` full battery recharges.

---

*Report generated by Adedeji Babalola's Cloud Carbon & GreenOps Estimator.*
`;
};

